import * as ProgressPrimitive from '@radix-ui/react-progress';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import { CheckCircle2, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { LogicTypeTargetStatusMeReadModel } from '@r/api/generated/client.schemas.ts';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import '@r/assets/styles/animation.css';

type TargetStatusType = LogicTypeTargetStatusMeReadModel;

interface ObjectiveItemProps {
  targetStatus?: TargetStatusType;
  isTargetPointLogic?: boolean;
}

const ObjectiveItem = ({ targetStatus, isTargetPointLogic = false }: ObjectiveItemProps) => {
  const { t } = useTranslation();

  const title = targetStatus?.logicTypeTarget?.reference || '';
  const description = targetStatus?.logicTypeTarget?.description;

  const currentValue = targetStatus?.value ?? 0;
  const targetValue = targetStatus?.targetValue ?? 0;
  const progressPercentage = targetValue > 0 ? (currentValue / targetValue) * 100 : 0;
  const radixValue = Math.min(100, Math.max(0, progressPercentage));
  const remainingValue = Math.max(0, targetValue - currentValue);
  const isAchieved = !!targetStatus?.achievedAt;
  const achievedDate = targetStatus?.achievedAt
    ? format(new Date(targetStatus.achievedAt), 'dd MMM yyyy', { locale: fr })
    : null;

  const fmtValue = (v: number) => ({
    style: isTargetPointLogic ? ('decimal' as const) : undefined,
    suffix: isTargetPointLogic ? ` ${pointsTranslation(t, v, false)}` : undefined
  });

  return (
    <div className="flex flex-col gap-2 px-4 py-3 rounded-lg bg-muted">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm font-bold">
          {t('performance.target.collapsible.objective')} "{title}"
        </p>
        {isAchieved && (
          <span className="flex items-center gap-1 shrink-0 text-xs font-medium text-green-600">
            <CheckCircle2 className="h-3 w-3" />
            {achievedDate
              ? t('performance.target.achieved.on', { date: achievedDate })
              : t('performance.target.achieved')}
          </span>
        )}
      </div>

      <div className="relative pt-8 pb-3 px-1">
        <div
          className="relative w-full"
          style={{
            transform: `translateX(-${100 - radixValue}%)`,
            animation: 'full-width-basic 0.5s ease-out forwards'
          }}
        >
          <div className="absolute right-0 -top-8 translate-x-[50%] flex flex-col items-center pointer-events-none">
            <div className="bg-primary text-primary-foreground text-xs font-medium px-2 py-0.5 rounded-md whitespace-nowrap">
              <FormattedNumber value={currentValue} {...fmtValue(currentValue)} />
            </div>
            <ChevronDown className="text-primary h-3 w-3 -mt-0.5" />
          </div>
        </div>

        <ProgressPrimitive.Root
          className="overflow-hidden relative w-full h-2 rounded-full bg-muted-foreground/20"
          value={radixValue}
        >
          <ProgressPrimitive.Indicator
            className="h-full w-full rounded-full bg-primary"
            style={{
              transform: `translateX(-${100 - radixValue}%)`,
              animation: 'full-width-basic 0.5s ease-out forwards'
            }}
          />
        </ProgressPrimitive.Root>

        <div className="relative mt-1.5 h-4">
          {!isAchieved && remainingValue > 0 && radixValue < 90 && (
            <div
              className="absolute -translate-x-1/2 text-xs text-muted-foreground whitespace-nowrap"
              style={{ left: `${radixValue}%` }}
            >
              <FormattedNumber value={remainingValue} {...fmtValue(remainingValue)} />{' '}
              {t('performance.target.progress.objective.remaining')}
            </div>
          )}
          <div className="absolute right-0 text-xs text-muted-foreground whitespace-nowrap">
            <FormattedNumber value={targetValue} {...fmtValue(targetValue)} />
          </div>
        </div>
      </div>

      {description && <p className="text-xs text-muted-foreground line-clamp-2">{description}</p>}
    </div>
  );
};

export { ObjectiveItem };
